import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import CourseNameBadge from '../../../components/common/CourseNameBadge';

// ─── Modal wrapper ─────────────────────────────────────────────────────────────
const BSModal = ({ show, title, size='', onClose, children, footer }) => {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}>
      <div className={`modal-dialog modal-dialog-scrollable${size ? ` modal-${size}` : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── Send-Topics-To-Students Dialog ───────────────────────────────────────────
//
// Shown after a faculty saves topic progress. Asks: "Send the topics you
// just completed to the present students of this batch by email?"
//
// Two actions: "Send Now" (calls the backend notify endpoint) and
// "Skip" (marks the daily record as skipped, no email sent).
// The dialog is informational only — closing it without clicking Send
// does NOT send emails (the backend defaults to "pending", and admin
// can re-fire from the daily record detail view).
const SendTopicsDialog = ({
  show, onClose, dailyRecord, onSent, onSkipped,
}) => {
  const [sending, setSending] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (show) { setResult(null); setSending(false); setSkipping(false); }
  }, [show, dailyRecord?._id]);

  if (!show || !dailyRecord) return null;

  const topics = dailyRecord.completedTopics || [];
  const activities = dailyRecord.activityEntries || [];
  const sentN = dailyRecord.notification?.sentCount || 0;
  const failedN = dailyRecord.notification?.failedCount || 0;
  const status = dailyRecord.notification?.status || 'pending';

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post(`/training/course-records/daily-records/${dailyRecord._id}/notify`);
      setResult(r.data?.data || { ok: true });
      onSent?.(r.data?.data);
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.message || e.message });
    }
    setSending(false);
  };
  const skip = async () => {
    setSkipping(true);
    try {
      await api.post(`/training/course-records/daily-records/${dailyRecord._id}/skip`);
      onSkipped?.();
      onClose();
    } catch (_) {}
    setSkipping(false);
  };

  return (
    <BSModal
      show onClose={onClose}
      title={<><i className="bi bi-envelope-paper me-2"/>Send Class Update to Students?</>}
      footer={
        result?.ok ? (
          <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
        ) : (
          <>
            <button className="btn btn-outline-secondary btn-sm" onClick={skip} disabled={sending || skipping}>
              {skipping ? <><span className="spinner-border spinner-border-sm me-1"/>Skipping…</> : 'Skip'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={sendNow} disabled={sending || skipping}>
              {sending ? <><span className="spinner-border spinner-border-sm me-1"/>Sending…</> : <><i className="bi bi-send me-1"/>Send Now</>}
            </button>
          </>
        )
      }
    >
      <div className="alert alert-success py-2 small mb-3">
        <i className="bi bi-check-circle-fill me-1"/>
        Class update saved successfully.
      </div>

      {result?.ok ? (
        <div className="text-center py-3">
          <i className="bi bi-check2-circle text-success" style={{ fontSize: 40 }}/>
          <h6 className="mt-2 mb-1">Email queue dispatched</h6>
          <p className="text-muted small mb-0">
            Sent to <strong>{result.sent || 0}</strong> students.
            {result.failed ? ` ${result.failed} failed.` : ''}
            {result.skipped ? ` ${result.skipped} were absent.` : ''}
          </p>
        </div>
      ) : (
        <>
          <p className="small mb-2">
            Send today's completed topics and class work/tests to the <strong>present students</strong> of this batch?
            Students marked absent on the attendance sheet will be skipped.
          </p>
          <div className="bg-light rounded p-2 mb-3" style={{ maxHeight: 180, overflowY: 'auto' }}>
            <div className="small fw-semibold mb-1">Topics in this update</div>
            <ol className="mb-0 ps-3 small">
              {topics.map((t, i) => (
                <li key={t.topicId || i}>
                  {t.topicName}
                  {t.moduleName ? <span className="text-muted"> — <em>{t.moduleName}</em></span> : null}
                </li>
              ))}
              {topics.length === 0 && <li className="text-muted">No completed topics in this update.</li>}
            </ol>
            {activities.length > 0 && (
              <>
                <div className="small fw-semibold mt-3 mb-1">Class work / tests</div>
                <ol className="mb-0 ps-3 small">
                  {activities.map((a, i) => (
                    <li key={a._id || i}>
                      {(a.type || 'activity').replace(/_/g, ' ')}
                      {a.topicName ? <span className="text-muted"> - {a.topicName}</span> : null}
                      {a.note ? <div className="text-muted">{a.note}</div> : null}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
          {status !== 'pending' && status !== 'failed' && (
            <div className="alert alert-warning small py-2">
              <i className="bi bi-info-circle me-1"/>
              This daily record was already <strong>{status}</strong>
              {sentN > 0 ? ` (${sentN} sent)` : ''}
              {failedN > 0 ? ` (${failedN} failed)` : ''}.
              Re-sending will email again.
            </div>
          )}
          {result && !result.ok && (
            <div className="alert alert-danger small py-2">
              <i className="bi bi-exclamation-triangle me-1"/>
              {result.error || 'Send failed.'}
            </div>
          )}
        </>
      )}
    </BSModal>
  );
};

// Timeline event types a faculty can record against a topic.
// Grouped visually in the dropdown so assessments stay separate from
// class-activity entries (workout / revision / doubt session etc).
const ENTRY_TYPES = [
  // Assessments
  { value: 'test',                label: 'Test',                 icon: 'bi-clipboard-check',    color: 'primary',   group: 'Assessment' },
  { value: 'practical',           label: 'Practical',            icon: 'bi-tools',              color: 'success',   group: 'Assessment' },
  { value: 'technical_interview', label: 'Technical Interview',  icon: 'bi-chat-square-dots',   color: 'info',      group: 'Assessment' },
  { value: 'viva',                label: 'Viva',                 icon: 'bi-mic',                color: 'warning',   group: 'Assessment' },
  // Class activities — days when no new content was covered
  { value: 'workout',             label: 'Workout / Homework',   icon: 'bi-pencil-square',      color: 'info',      group: 'Class Activity' },
  { value: 'class_activity',      label: 'Class Activity',       icon: 'bi-people',             color: 'success',   group: 'Class Activity' },
  { value: 'revision',            label: 'Revision',             icon: 'bi-arrow-counterclockwise', color: 'warning', group: 'Class Activity' },
  { value: 'doubt_session',       label: 'Doubt Clearing',       icon: 'bi-question-circle',    color: 'primary',   group: 'Class Activity' },
  { value: 'project_work',        label: 'Project Work',         icon: 'bi-kanban',             color: 'info',      group: 'Class Activity' },
  // Catch-all
  { value: 'other',               label: 'Other',                icon: 'bi-bookmark',           color: 'secondary', group: 'Other' },
];
const entryLabel = (v) => ENTRY_TYPES.find(t => t.value === v)?.label || 'Event';
const entryIcon  = (v) => ENTRY_TYPES.find(t => t.value === v)?.icon  || 'bi-calendar-event';
const entryColor = (v) => ENTRY_TYPES.find(t => t.value === v)?.color || 'secondary';

// ─── Log Activity Dialog ─────────────────────────────────────────────────────
// Single, page-level form for recording what happened in a class today.
// User doesn't need to drill into a specific topic — they pick the topic from
// a dropdown (auto-defaulted to "Next to cover" so the common case is one click).
const LogActivityDialog = ({ show, onClose, allTopics, courseId, batchId, onSubmit }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [type, setType] = useState('workout');
  const [topicId, setTopicId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    // Default to the first incomplete topic — most commonly what today's
    // activity relates to (either covering it, or giving a workout on it).
    const nextTopic = (allTopics || []).find(t => !t.progress?.isCompleted);
    setTopicId(nextTopic?._id || (allTopics?.[0]?._id) || '');
    setDate(new Date().toISOString().split('T')[0]);
    setType('workout');
    setNote('');
    setSaving(false);
  }, [show, allTopics]);

  const selectedTopic = (allTopics || []).find(t => t._id === topicId);

  const handleSubmit = async () => {
    if (!topicId || !date || !type) return;
    setSaving(true);
    try {
      await onSubmit({
        courseId,
        moduleItemId: selectedTopic?.moduleId,
        topicItemId: topicId,
        topicTitle: selectedTopic?.title,
        batchId,
        date, type, note,
      });
      onClose();
    } catch (_) {}
    setSaving(false);
  };

  return (
    <BSModal
      show={show} onClose={onClose} size="lg"
      title="Log Today's Activity"
      footer={<>
        <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving || !topicId || !date}>
          {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Saving…</> : <><i className="bi bi-check-lg me-1"/>Save Entry</>}
        </button>
      </>}
    >
      <div className="alert alert-info py-2 small mb-3">
        <i className="bi bi-info-circle me-1"/>
        Log what was done in class today. If you didn't cover new content,
        logging a <strong>Workout</strong> / <strong>Revision</strong> / etc.
        keeps the planned topic unfinished — it stays in the "Next to cover" slot.
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Date <span className="text-danger">*</span></label>
          <input type="date" className="form-control form-control-sm" value={date} onChange={e => setDate(e.target.value)} max={new Date().toISOString().split('T')[0]}/>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Activity Type <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={type} onChange={e => setType(e.target.value)}>
            {['Assessment', 'Class Activity', 'Other'].map(group => (
              <optgroup key={group} label={group}>
                {ENTRY_TYPES.filter(t => t.group === group).map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Topic <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={topicId} onChange={e => setTopicId(e.target.value)}>
            <option value="">Select topic...</option>
            {(allTopics || []).map(t => (
              <option key={t._id} value={t._id}>
                {t.moduleTitle ? `${t.moduleTitle} → ` : ''}{t.title}
                {t.progress?.isCompleted ? ' ✓' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label small fw-semibold">Note <span className="text-muted">(optional)</span></label>
          <textarea
            className="form-control form-control-sm" rows={2}
            value={note} onChange={e => setNote(e.target.value)}
            placeholder={
              type === 'workout' ? 'e.g. Assigned 5 problems on loops'
              : type === 'test' ? 'e.g. 15/20 average, top score 19'
              : type === 'practical' ? 'e.g. Built todo app using hooks'
              : type === 'revision' ? 'e.g. Revised state management'
              : type === 'doubt_session' ? 'e.g. Covered useEffect dependencies'
              : 'Details, score, remarks...'
            }
          />
        </div>
      </div>
    </BSModal>
  );
};

// ─── Topic Row with progress controls ─────────────────────────────────────────
const TopicRow = ({ topic, moduleId, courseId, batchId, isReadOnly, onProgressUpdate, onTestEntry, onDeleteEntry }) => {
  const prog = topic.progress || {};
  const [saving, setSaving] = useState(false);
  const [remarks, setRemarks] = useState(prog.remarks || '');
  const [workoutNotes, setWorkoutNotes] = useState(prog.workoutNotes || '');
  const [expanded, setExpanded] = useState(false);
  const [showTestEntry, setShowTestEntry] = useState(false);
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);
  const [testNote, setTestNote] = useState('');
  const [testType, setTestType] = useState('test');

  const toggle = async () => {
    if (isReadOnly) return;
    setSaving(true);
    try {
      await onProgressUpdate({
        courseId, moduleItemId: moduleId, topicItemId: topic._id,
        moduleTitle: '', topicTitle: topic.title,
        isCompleted: !prog.isCompleted, remarks, batchId, workoutNotes,
      });
    } catch(_) {}
    setSaving(false);
  };

  const saveRemarks = async () => {
    setSaving(true);
    try {
      await onProgressUpdate({
        courseId, moduleItemId: moduleId, topicItemId: topic._id,
        topicTitle: topic.title, isCompleted: prog.isCompleted || false,
        remarks, batchId, workoutNotes,
      });
    } catch(_) {}
    setSaving(false);
  };

  const addTestEntry = async () => {
    if (!testDate) return;
    try {
      await onTestEntry({ courseId, moduleItemId: moduleId, topicItemId: topic._id, topicTitle: topic.title, batchId, date: testDate, note: testNote, type: testType });
      setTestNote(''); setTestType('test'); setShowTestEntry(false);
    } catch(_) {}
  };

  // Build a unified timeline: "content covered" (from completedAt) + all
  // testEntries — sorted by date DESC (newest first).
  const buildTimeline = () => {
    const events = [];
    if (prog.isCompleted && prog.completedAt) {
      events.push({
        _id: 'content-covered',
        date: prog.completedAt,
        type: 'content_covered',
        note: prog.remarks || '',
        meta: true, // marker so we don't offer a delete button
      });
    }
    (prog.testEntries || []).forEach(e => events.push({
      _id: e._id,
      date: e.date,
      type: e.type || 'test',
      note: e.note || '',
    }));
    return events.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const timeline = buildTimeline();

  return (
    <div className={`border rounded mb-1 ${prog.isCompleted ? 'border-success bg-success bg-opacity-10' : ''}`}>
      <div className="d-flex align-items-center gap-2 p-2">
        {!isReadOnly ? (
          <button className={`btn btn-sm ${prog.isCompleted ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={toggle} disabled={saving} style={{width:'32px',height:'32px',padding:0}}>
            {saving ? <span className="spinner-border spinner-border-sm"/> : <i className={`bi ${prog.isCompleted ? 'bi-check-circle-fill' : 'bi-circle'}`}/>}
          </button>
        ) : (
          <span className={`badge ${prog.isCompleted ? 'bg-success' : 'bg-light text-dark'}`}>
            {prog.isCompleted ? '✓' : '○'}
          </span>
        )}
        <span className={`flex-grow-1 small ${prog.isCompleted ? 'text-decoration-line-through text-muted' : ''}`}>
          {topic.title}
        </span>
        {prog.completedAt && <small className="text-muted">{new Date(prog.completedAt).toLocaleDateString()}</small>}
        {(prog.testEntries?.length > 0) && (
          <span className="badge bg-info small">{prog.testEntries.length} test{prog.testEntries.length > 1 ? 's' : ''}</span>
        )}
        <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => setExpanded(p => !p)}>
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} style={{fontSize:'0.7rem'}}/>
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-2 border-top">
          {!isReadOnly && (
            <div className="row g-2 mt-1">
              <div className="col-md-6">
                <label className="form-label small mb-1">Remarks</label>
                <input className="form-control form-control-sm" value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  onBlur={saveRemarks} placeholder="Add remarks..."/>
              </div>
              <div className="col-md-6">
                <label className="form-label small mb-1">Workout Notes</label>
                <input className="form-control form-control-sm" value={workoutNotes}
                  onChange={e => setWorkoutNotes(e.target.value)}
                  onBlur={saveRemarks} placeholder="Workout notes..."/>
              </div>
            </div>
          )}
          {isReadOnly && prog.remarks && (
            <div className="mt-1 small text-muted">Remarks: {prog.remarks}</div>
          )}

          {/* Unified timeline — content-covered + test/practical/interview
              entries sorted by date, newest first. */}
          {timeline.length > 0 && (
            <div className="mt-2">
              <small className="fw-semibold text-muted d-block mb-2">
                <i className="bi bi-clock-history me-1"/>Activity Timeline
              </small>
              <div className="position-relative ps-3" style={{ borderLeft: '2px solid #e5e7eb' }}>
                {timeline.map((e) => {
                  const isCovered = e.type === 'content_covered';
                  const color = isCovered ? 'success' : entryColor(e.type);
                  const icon  = isCovered ? 'bi-check-circle-fill' : entryIcon(e.type);
                  const label = isCovered ? 'Content Covered' : entryLabel(e.type);
                  return (
                    <div key={e._id} className="position-relative mb-2" style={{ paddingLeft: 4 }}>
                      <span
                        className={`position-absolute bg-${color}`}
                        style={{ width: 10, height: 10, borderRadius: '50%', left: -19, top: 6, border: '2px solid #fff' }}
                      />
                      <div className="d-flex align-items-start gap-2 flex-wrap">
                        <i className={`bi ${icon} text-${color}`}/>
                        <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle small`}>
                          {label}
                        </span>
                        <span className="small text-muted">
                          {new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        {e.note && <span className="small flex-grow-1">— {e.note}</span>}
                        {!isReadOnly && !e.meta && onDeleteEntry && (
                          <button
                            className="btn btn-sm btn-link text-danger p-0 ms-auto"
                            style={{ fontSize: 11 }}
                            onClick={() => onDeleteEntry({ entryId: e._id, courseId, topicItemId: topic._id, batchId })}
                            title="Remove entry"
                          >
                            <i className="bi bi-x-lg"/>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!isReadOnly && (
            <div className="mt-2">
              {showTestEntry ? (
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Type</label>
                    <select className="form-select form-select-sm" value={testType} onChange={e => setTestType(e.target.value)}>
                      {['Assessment', 'Class Activity', 'Other'].map(group => (
                        <optgroup key={group} label={group}>
                          {ENTRY_TYPES.filter(t => t.group === group).map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Date</label>
                    <input className="form-control form-control-sm" type="date" value={testDate} onChange={e => setTestDate(e.target.value)}/>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Note <span className="text-muted">(optional)</span></label>
                    <input className="form-control form-control-sm" value={testNote} onChange={e => setTestNote(e.target.value)} placeholder="e.g. score, feedback, remarks..."/>
                  </div>
                  <div className="col-md-2 d-flex gap-1">
                    <button className="btn btn-primary btn-sm flex-grow-1" onClick={addTestEntry}>Add</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowTestEntry(false)}>
                      <i className="bi bi-x"/>
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowTestEntry(true)}>
                  <i className="bi bi-plus-lg me-1"/>Log Event (Test · Practical · Workout · Activity · Doubt Session…)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Progress Report Dialog ────────────────────────────────────────────────────
const ProgressReportDialog = ({ show, courses, onClose }) => {
  const [courseId, setCourseId] = useState('');
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);

  const fetchReport = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const r = await api.get('/training/course-records/progress-report', { params: { courseId } });
      setReport(r.data?.data);
    } catch(_) {}
    setLoading(false);
  };

  return (
    <BSModal show={show} title="Faculty Progress Report" size="lg" onClose={onClose}>
      <div className="d-flex gap-2 mb-3">
        <select className="form-select form-select-sm" value={courseId} onChange={e => setCourseId(e.target.value)}>
          <option value="">Select Course</option>
          {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={fetchReport} disabled={!courseId || loading}>
          {loading ? <span className="spinner-border spinner-border-sm"/> : 'Load'}
        </button>
      </div>

      {report && (
        <>
          <p className="text-muted small">Total topics: {report.totalTopics}</p>
          {report.report.length === 0 ? (
            <p className="text-center text-muted py-3">No progress recorded yet.</p>
          ) : (
            report.report.map(({ faculty, records, completedCount }) => (
              <div key={faculty._id} className="card mb-3">
                <div className="card-header py-2 d-flex justify-content-between">
                  <span className="fw-semibold">{faculty.firstName} {faculty.lastName}</span>
                  <span className="badge bg-primary">{completedCount}/{report.totalTopics} completed</span>
                </div>
                <div className="card-body p-0">
                  <div className="progress" style={{height:'6px', borderRadius:0}}>
                    <div className="progress-bar bg-success" style={{width: `${(completedCount / report.totalTopics) * 100}%`}}/>
                  </div>
                  <div className="p-2">
                    {records.filter(r => r.isCompleted).map((r, i) => (
                      <div key={i} className="d-flex gap-2 align-items-center small py-1">
                        <i className="bi bi-check-circle-fill text-success"/>
                        <span>{r.topicTitle || r.topicItemId}</span>
                        {r.remarks && <span className="text-muted">— {r.remarks}</span>}
                        {r.completedAt && <span className="ms-auto text-muted">{new Date(r.completedAt).toLocaleDateString()}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </BSModal>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function CourseRecordsPage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const isStaff = ['admin','manager','team_lead'].includes(role);
  // Mirror the backend's TEACHING_ROLES — without 'teacher'/'trainer' the
  // legacy Trainer-role users (renamed to "Faculty" but key kept) wouldn't
  // be recognised as faculty in any of the per-faculty UI branches.
  const isFaculty = ['faculty','teacher','trainer'].includes(role);

  const [courses, setCourses]   = useState([]);
  const [batches, setBatches]   = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [facultyId, setFacultyId] = useState('');   // admin-only: whose records to view/edit
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [batchId, setBatchId]   = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [content, setContent]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [alert, setAlert]       = useState({ show:false, msg:'', type:'success' });
  // ── Daily topic notification state ────────────────────────────────────────
  // `pendingDailyRecord` is set right after a topic flip completes. We then
  // fetch the corresponding daily record and open the "Send to students?"
  // dialog so the faculty can either send the email or skip it.
  const [pendingDailyRecord, setPendingDailyRecord] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);

  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  useEffect(() => {
    api.get('/training/course-records/courses')
      .then(r => setCourses(r.data?.data?.courses || []))
      .catch(() => {});
    // Staff (admin/manager/team_lead) can view/edit on behalf of any faculty.
    // Pull every teaching role-key — without 'teacher'/'trainer' here, users
    // on the legacy Trainer role wouldn't appear in the picker at all.
    if (isStaff) {
      api.get('/users', { params: { roleKey: 'faculty,teacher,trainer', limit: 500 } })
        .then(r => setFaculties(r.data?.records || r.data?.data || []))
        .catch(() => {});
    }
  }, [isStaff]);

  // Load batches whenever the target user changes.
  // Team lead "my own records" (facultyId='') → scope to their own assigned batches.
  // Admin/Manager "my own records" → return all open batches (global view).
  // Any staff with a specific facultyId selected → that faculty's batches.
  useEffect(() => {
    const params = {};
    if (isStaff && facultyId) {
      params.facultyId = facultyId;
    } else if (role === 'team_lead' && !facultyId) {
      params.facultyId = user?.id || user?._id;
    }
    if (!showCompleted) params.status = 'ongoing,started,upcoming';
    api.get('/training/course-records/batches', { params })
      .then(r => setBatches(r.data?.data?.batches || []))
      .catch(() => setBatches([]));
  }, [isStaff, facultyId, role, user, showCompleted]);

  const fetchContent = useCallback(async () => {
    if (!courseId && !batchId) return;
    setLoading(true);
    try {
      const params = {};
      if (batchId) params.batchId = batchId;
      else params.courseId = courseId;
      // Admin/manager/team_lead: include facultyId so we load that faculty's
      // progress instead of the admin's own (which is typically empty).
      if (isStaff && facultyId) params.facultyId = facultyId;
      const r = await api.get('/training/course-records/content', { params });
      setContent(r.data?.data);
      setIsReadOnly(r.data?.data?.isReadOnly || false);
    } catch(e) { showAlert(e.response?.data?.message || 'Failed to load content.', 'danger'); }
    setLoading(false);
  }, [courseId, batchId, isStaff, facultyId]);

  const handleProgressUpdate = async (data) => {
    // Staff editing on behalf of a faculty — attach facultyId so backend
    // writes the progress under the correct user.
    const payload = (isStaff && facultyId) ? { ...data, facultyId } : data;
    const wasCompletingNew = !!data.isCompleted;
    await api.patch('/training/course-records/topic-progress', payload);
    fetchContent();

    // If the faculty just flipped a topic to "completed" for a batch
    // record, surface the "Send topics to present students?" dialog.
    // The backend has just (silently) upserted the daily record — we
    // fetch it so the dialog can render with real data.
    //
    // Skip when:
    //   - record is course-only (no batch) — there's no roster to email
    //   - faculty is "un-completing" a topic (isCompleted=false) — no
    //     new content to announce
    //   - the toggle is disabled in tenant settings (we still open the
    //     dialog, but the Send button respects the disabled flag and
    //     the API call is a no-op)
    if (wasCompletingNew && batchId && data.courseId) {
      try {
        const today = new Date().toISOString().split('T')[0];
        const params = {
          courseId: data.courseId, batchId, date: today,
        };
        if (isStaff && facultyId) params.facultyId = facultyId;
        const r = await api.get('/training/course-records/daily-records', { params });
        if (r.data?.data?.record) {
          setPendingDailyRecord(r.data.data.record);
          setShowSendDialog(true);
        }
      } catch (_) { /* non-blocking — faculty still gets progress update */ }
    }
  };

  const handleTestEntry = async (data) => {
    const payload = (isStaff && facultyId) ? { ...data, facultyId } : data;
    await api.post('/training/course-records/test-entry', payload);
    fetchContent();
  };

  const handleDeleteEntry = async ({ entryId, courseId: cid, topicItemId, batchId: bid }) => {
    if (!window.confirm('Remove this entry from the timeline?')) return;
    const params = { courseId: cid, topicItemId };
    if (bid) params.batchId = bid;
    if (isStaff && facultyId) params.facultyId = facultyId;
    try {
      await api.delete(`/training/course-records/test-entry/${entryId}`, { params });
      fetchContent();
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to delete entry', 'danger'); }
  };

  const stats = content?.stats || { total: 0, completed: 0 };
  const pct = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show:false }))}/>

      <PageHeader
        title="Course Records"
        icon="bi-journal-check"
        subtitle="Track topic completion and progress"
        actions={isStaff && (
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowReport(true)}>
            <i className="bi bi-bar-chart me-1"/>Progress Report
          </button>
        )}
      />

      {/* Selectors */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            {/* Admin / manager / team_lead faculty picker — decides whose
                progress records to view and edit. Leaving it blank means
                "my own" (useful when an admin also teaches a batch). */}
            {isStaff && (
              <div className="col-md-4">
                <label className="form-label mb-0 small fw-semibold">
                  Faculty <span className="text-muted" style={{ fontWeight: 400 }}>(whose records to edit)</span>
                </label>
                <select className="form-select form-select-sm" value={facultyId}
                  onChange={e => setFacultyId(e.target.value)}>
                  <option value="">— My own records —</option>
                  {faculties.map(f => (
                    <option key={f._id} value={f._id}>
                      {f.firstName} {f.lastName} {f.email ? `· ${f.email}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {batches.length > 0 && (
              <div className="col-md-4">
                <label className="form-label mb-0 small fw-semibold">
                  Batch <span className="text-muted" style={{ fontWeight: 400 }}>({batches.length} available)</span>
                </label>
                <select className="form-select form-select-sm" value={batchId}
                  onChange={e => {
                    const id = e.target.value;
                    setBatchId(id);
                    if (id) {
                      const b = batches.find(x => x._id === id);
                      const cid = b?.course?._id || b?.course;
                      if (cid) setCourseId(typeof cid === 'object' ? cid.toString() : cid);
                    } else {
                      setCourseId('');
                    }
                  }}>
                  <option value="">Select Batch</option>
                  {batches.map(b => {
                    const studentList = (b.studentNames || []).filter(Boolean);
                    const studentSummary = studentList.length > 0
                      ? ` — ${studentList.slice(0, 2).join(', ')}${studentList.length > 2 ? `, +${b.studentCount - 2} more` : ''}`
                      : '';
                    return (
                      <option key={b._id} value={b._id}>
                        {b.batchNumber} — {b.course?.courseName || b.course?.name} ({b.studentCount || 0} student{(b.studentCount || 0) !== 1 ? 's' : ''}){studentSummary}
                      </option>
                    );
                  })}
                </select>
                {batchId && (() => {
                  const b = batches.find(x => x._id === batchId);
                  if (!b) return null;
                  const t = b.timing || {};
                  const timeStr = t.startTime
                    ? `${t.startTime}${t.endTime ? ` – ${t.endTime}` : ''}`
                    : t.startHour
                    ? `${t.startHour}:00 ${t.startPeriod || 'AM'}`
                    : null;
                  const days = b.schedule?.length > 0
                    ? b.schedule.map(s => s.day?.slice(0,3).toUpperCase()).join(' · ')
                    : '';
                  return (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {timeStr && (
                        <span className="badge bg-primary text-white fw-normal">
                          <i className="bi bi-clock me-1"/>{timeStr}
                        </span>
                      )}
                      <CourseNameBadge courseName={b.course?.courseName || b.course?.name || 'Course'} courseCode={b.course?.courseCode || ''} courseColor={b.course?.courseColor} compact />
                      {b.classType && (
                        <span className="badge bg-info text-white fw-normal">
                          <i className="bi bi-tag me-1"/>{b.classType}
                        </span>
                      )}
                      {b.classroom && (
                        <span className="badge bg-secondary text-white fw-normal">
                          <i className="bi bi-door-open me-1"/>{b.classroom}
                        </span>
                      )}
                      {days && (
                        <span className="badge bg-success text-white fw-normal">
                          <i className="bi bi-calendar3 me-1"/>{days}
                        </span>
                      )}
                      {!timeStr && !b.classType && !b.classroom && !days && (
                        <span className="text-muted small">No details set</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="col-md-4">
              <label className="form-label mb-0 small fw-semibold">
                Course {batchId && <span className="text-muted fw-normal">(auto from batch)</span>}
              </label>
              <select className="form-select form-select-sm" value={courseId}
                disabled={!!batchId}
                onChange={e => { setCourseId(e.target.value); setBatchId(''); }}>
                <option value="">Select Course</option>
                {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
              </select>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={fetchContent} disabled={(!courseId && !batchId) || loading}>
                {loading ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-search me-1"/>Load</>}
              </button>
            </div>
          </div>
          {isStaff && facultyId && (
            <div className="small text-muted mt-2">
              <i className="bi bi-info-circle me-1" />
              Editing course records on behalf of <strong>{faculties.find(f => f._id === facultyId) ? `${faculties.find(f => f._id === facultyId).firstName} ${faculties.find(f => f._id === facultyId).lastName}` : 'selected faculty'}</strong>.
            </div>
          )}
          <div className="mt-2">
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="checkbox" id="showCompleted" checked={showCompleted}
                onChange={e => setShowCompleted(e.target.checked)} />
              <label className="form-check-label small" htmlFor="showCompleted">Show Completed</label>
            </div>
          </div>
        </div>
      </div>

      {/* Stats + "Log Today's Activity" button + Next-topic hint */}
      {content && (() => {
        // Find the first incomplete topic across all modules — this is what
        // should be covered next. When the faculty logs a workout/test/etc.
        // today without marking a topic complete, this banner makes it obvious
        // which topic has effectively been pushed to the next session.
        const allTopics = (content.record?.modules || []).flatMap(mod =>
          (mod.topics || []).map(t => ({ ...t, moduleTitle: mod.title, moduleId: mod._id }))
        );
        const nextTopic = allTopics.find(t => !t.progress?.isCompleted);

        // Collect every timeline event (content covered + all testEntries)
        // across the whole course, so the activity log is one unified feed.
        const allEvents = [];
        allTopics.forEach(t => {
          const prog = t.progress || {};
          if (prog.isCompleted && prog.completedAt) {
            allEvents.push({
              _id: `cc-${t._id}`,
              date: prog.completedAt,
              type: 'content_covered',
              topicTitle: t.title,
              moduleTitle: t.moduleTitle,
              note: prog.remarks || '',
              meta: true,
            });
          }
          (prog.testEntries || []).forEach(e => allEvents.push({
            _id: e._id,
            date: e.date,
            type: e.type || 'test',
            topicTitle: t.title,
            moduleTitle: t.moduleTitle,
            topicItemId: t._id,
            moduleItemId: t.moduleId,
            note: e.note || '',
          }));
        });
        allEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

        return (
          <>
            <div className="card mb-3">
              <div className="card-body py-2">
                <div className="d-flex align-items-center gap-3">
                  {content.batch && (
                    <span className="fw-semibold">{content.batch.batchNumber} — {content.batch.course?.name}</span>
                  )}
                  <span className="text-muted small">{stats.completed}/{stats.total} topics completed</span>
                  <div className="flex-grow-1">
                    <div className="progress" style={{height:'8px'}}>
                      <div className="progress-bar bg-success" style={{width:`${pct}%`}}/>
                    </div>
                  </div>
                  <span className="fw-bold">{pct}%</span>
                  {!isReadOnly && (
                    <button className="btn btn-primary btn-sm" onClick={() => setShowLogDialog(true)}>
                      <i className="bi bi-plus-lg me-1"/>Log Today's Activity
                    </button>
                  )}
                  {isReadOnly && <span className="badge bg-secondary">Read Only</span>}
                </div>
                {nextTopic && (
                  <div className="mt-2 small text-muted">
                    <i className="bi bi-arrow-right-circle me-1 text-primary"/>
                    <strong>Next to cover:</strong> {nextTopic.moduleTitle} → {nextTopic.title}
                    <span className="text-muted ms-2">(missed content automatically rolls forward)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Course-wide activity timeline (latest first) */}
            {allEvents.length > 0 && (
              <div className="card mb-3">
                <div className="card-header py-2 d-flex justify-content-between align-items-center">
                  <span className="fw-semibold"><i className="bi bi-clock-history me-2 text-primary"/>Activity Log</span>
                  <span className="text-muted small">{allEvents.length} events</span>
                </div>
                <div className="card-body py-2">
                  <div className="position-relative ps-3" style={{ borderLeft: '2px solid #e5e7eb' }}>
                    {allEvents.slice(0, 30).map((e) => {
                      const isCovered = e.type === 'content_covered';
                      const color = isCovered ? 'success' : entryColor(e.type);
                      const icon  = isCovered ? 'bi-check-circle-fill' : entryIcon(e.type);
                      const label = isCovered ? 'Content Covered' : entryLabel(e.type);
                      return (
                        <div key={e._id} className="position-relative mb-2" style={{ paddingLeft: 4 }}>
                          <span
                            className={`position-absolute bg-${color}`}
                            style={{ width: 10, height: 10, borderRadius: '50%', left: -19, top: 6, border: '2px solid #fff' }}
                          />
                          <div className="d-flex align-items-start gap-2 flex-wrap small">
                            <span className="text-muted" style={{ minWidth: 90 }}>
                              {new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </span>
                            <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle`}>
                              <i className={`bi ${icon} me-1`}/>{label}
                            </span>
                            <span className="fw-semibold">{e.topicTitle}</span>
                            {e.note && <span className="text-muted">— {e.note}</span>}
                            {!isReadOnly && !e.meta && (
                              <button
                                className="btn btn-sm btn-link text-danger p-0 ms-auto"
                                style={{ fontSize: 11 }}
                                onClick={() => handleDeleteEntry({ entryId: e._id, courseId: content.record.courseId, topicItemId: e.topicItemId, batchId })}
                                title="Remove entry"
                              >
                                <i className="bi bi-x-lg"/>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {allEvents.length > 30 && (
                    <div className="text-center small text-muted mt-2">Showing latest 30. Older entries available in the topic rows below.</div>
                  )}
                </div>
              </div>
            )}

            {/* Log Activity Dialog */}
            <LogActivityDialog
              show={showLogDialog}
              onClose={() => setShowLogDialog(false)}
              allTopics={allTopics}
              courseId={content.record?.courseId}
              batchId={batchId}
              onSubmit={handleTestEntry}
            />
          </>
        );
      })()}

      {/* Modules + Topics */}
      {!content ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-journal-check fs-1 d-block mb-2"/>
          Select a course or batch to view records
        </div>
      ) : loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"/></div>
      ) : content.record?.modules?.length === 0 ? (
        <div className="text-center py-5 text-muted">No modules found for this course.</div>
      ) : (
        content.record?.modules?.map(mod => (
          <div key={mod._id} className="card mb-3">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold"><i className="bi bi-collection me-2 text-primary"/>{mod.title}</span>
              <span className="badge bg-light text-dark border">
                {mod.topics.filter(t => t.progress?.isCompleted).length}/{mod.topics.length} done
              </span>
            </div>
            <div className="card-body p-2">
              {mod.topics.length === 0 ? (
                <p className="text-muted text-center small py-2 mb-0">No topics in this module.</p>
              ) : (
                mod.topics.map(t => (
                  <TopicRow
                    key={t._id}
                    topic={t}
                    moduleId={mod._id}
                    courseId={content.record.courseId}
                    batchId={batchId}
                    isReadOnly={isReadOnly}
                    onProgressUpdate={handleProgressUpdate}
                    onTestEntry={handleTestEntry}
                    onDeleteEntry={handleDeleteEntry}
                  />
                ))
              )}
            </div>
          </div>
        ))
      )}

      <ProgressReportDialog show={showReport} courses={courses} onClose={() => setShowReport(false)}/>

      {/* Send-topics-to-students dialog — surfaced after a faculty
          completes a topic for a batch record. Lets them fire the
          email send to the present students or skip it. */}
      <SendTopicsDialog
        show={showSendDialog}
        dailyRecord={pendingDailyRecord}
        onClose={() => { setShowSendDialog(false); setPendingDailyRecord(null); }}
        onSkipped={() => { setShowSendDialog(false); setPendingDailyRecord(null); }}
        onSent={(res) => {
          // Update the local daily record so re-opening the dialog
          // reflects the new sent/failed counts.
          if (pendingDailyRecord) {
            setPendingDailyRecord({
              ...pendingDailyRecord,
              notification: {
                ...(pendingDailyRecord.notification || {}),
                status: res?.failed > 0 && res?.sent === 0 ? 'failed' : 'sent',
                sentCount: res?.sent || 0,
                failedCount: res?.failed || 0,
                sentAt: new Date().toISOString(),
              },
            });
          }
        }}
      />
    </div>
  );
}
